import { useState } from 'react';
import { Tooltip } from 'primereact/tooltip';
import { Dropdown } from 'primereact/dropdown';
import { Paginator } from 'primereact/paginator';
import { Chip, Spinner, Typography } from '@uni-of-exeter/digital.lib.react.ui.kit';

import './styles/resource-table.css';
import { APP_TEXT } from '../const/app-text';
import ContinueModal from './modals/continue-modal';
import ResourceTypeModal from './modals/resource-type';
import LikesInfoModal from './modals/likes-info-modal';
import ProgressInfoModal from './modals/progress-info-modal';
import ResourceLocationModal from './modals/resource-location';
import EvaluateResourceModal from './modals/evaluate-resource';
import { getLocationText } from '../utils/resource-table-helpers';
import { useResourceTable } from '../hooks/custom/use-resource-table';
import HideRecommendationModal from './modals/hide-recommendation-modal';
import { useBookmarks } from '../hooks/custom/bookmarking/use-bookmarks';
import { BookmarkColumnBody } from './resource-table/bookmark-resource-column';
import { PutUserResourceInteractionBody } from '../services/learning-api/schema';
import { RemoveBookmarkColumnBody } from './resource-table/remove-bookmark-column';
import { ResourceTableProps, EnhancedResourceTableData } from './shared/resource-table-types';
import { MobileColumnHeaderInfoButton } from './resource-table/mobile-column-header-information-button';

type ProgressStatus = PutUserResourceInteractionBody['status'];

const text = APP_TEXT.components.resourceTable;
const externalResourceModal = APP_TEXT.components.externalResourceModal;

export const ResourceTableMobile = ({
  resources,
  isLoading = false,
  isError = false,
  getCategoryColor,
  source,
  rowsToShow = 10,
  setCurrentPage,
  currentPage,
  totalItems,
  defaultPagination = false,
  enableRatingSystem = false,
  enableHideRecommendations = false,
  enableBookmarkDeleteColumn = false,
  enableResourceBookmarking = false,
}: ResourceTableProps) => {
  const [first, setFirst] = useState(0);
  const { handleCreateBookmark, handleDeleteBookmark } = useBookmarks();

  const {
    // State
    progress,
    selectedExternalResource,
    evaluateResourceModalOpen,
    resourceToEvaluate,
    tableData,
    progressOptions,

    // Modal states
    recommendationToHide,
    hideRecommendationModal,
    confirmHideRecommendation,
    setRecommendationToHide,

    // Modal states
    resourceTypeModal,
    resourceLocationModal,
    progressModal,
    likesModal,

    // Event handlers
    handleLikeClick,
    handleProgressChange,
    handleViewClick,
    handleExternalContinue,
    getProgressDropdownClass,

    // Setters
    setSelectedExternalResource,
    setEvaluateResourceModalOpen,
    setResourceToEvaluate,
  } = useResourceTable({
    resources,
    source,
    enableRatingSystem,
    getCategoryColor,
  });

  // Create enhanced table data with JSX category component
  const enhancedTableData: EnhancedResourceTableData[] = tableData.map((data) => ({
    ...data,
    titleString: data.title,
    title: (
      <a
        rel="noreferrer"
        className="resourceTitleUrl resourceTitleTarget"
        href={data.url}
        target="_blank"
        aria-label={data.title}
        tabIndex={0}
        onClick={(e) => {
          e.preventDefault();
          handleViewClick(data);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            handleViewClick(data);
          }
        }}
      >
        {source == 'RecommendationPage' && data.isRoleDeptMatched === true && (
          <img
            src={text.roleDepartmentMatch.icon}
            alt={text.roleDepartmentMatch.alt}
          />
        )}
        {data.title}
      </a>
    ),
    category: (
      <Chip
        className="category"
        label={data.category}
        backgroundColor={getCategoryColor(data.category)}
        role="presentation"
        style={{ border: `1px solid ${getCategoryColor(data.category)}` }}
      />
    ),
  }));

  if (isLoading) {
    return (
      <div className="flexCenter">
        <Spinner aria-label="Loading resource information..." />
      </div>
    );
  }

  if (isError) {
    return <div>{text.error}</div>;
  }

  return (
    <div
      className="resourceTable"
      id="resourceTable"
    >
      <div className="resourceTableMobileHeader">
        <div className="resourceTableHeader">
          <Typography
            variant="body"
            className="resourceTableHeaderText mobile"
            onClick={resourceTypeModal.onTrue}
          >
            {text.headers.resourceType}
          </Typography>

          <MobileColumnHeaderInfoButton
            text={{ ariaLabel: text.accessibility.resourceTypeIIcon }}
            onClick={resourceTypeModal.onTrue}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                resourceTypeModal.onTrue();
              }
            }}
          />
        </div>
        <div className="resourceTableHeader">
          <Typography
            variant="body"
            className="resourceTableHeaderText mobile"
            onClick={resourceLocationModal.onTrue}
          >
            {text.headers.resourceLocation}
          </Typography>

          <MobileColumnHeaderInfoButton
            text={{ ariaLabel: text.accessibility.resourceLocationIIcon }}
            onClick={resourceLocationModal.onTrue}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                resourceLocationModal.onTrue();
              }
            }}
          />
        </div>
        <div className="resourceTableHeader">
          <Typography
            variant="body"
            className="resourceTableHeaderText mobile"
            onClick={progressModal.onTrue}
          >
            {text.headers.progress}
          </Typography>

          <MobileColumnHeaderInfoButton
            text={{ ariaLabel: text.accessibility.progressIcon }}
            onClick={progressModal.onTrue}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                progressModal.onTrue();
              }
            }}
          />
        </div>
        {enableRatingSystem && (
          <div className="resourceTableHeader">
            <Typography
              variant="body"
              className="resourceTableHeaderText mobile"
              onClick={likesModal.onTrue}
            >
              {text.headers.likes}
            </Typography>

            <MobileColumnHeaderInfoButton
              text={{ ariaLabel: text.accessibility.likesIcon }}
              onClick={likesModal.onTrue}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  likesModal.onTrue();
                }
              }}
            />
          </div>
        )}
      </div>

      <div className="resourceTableMobileContainer">
        {enhancedTableData.slice(first, first + rowsToShow).map((data) => {
          // Extract base data for handleViewClick
          const baseData = {
            id: data.id,
            title: typeof data.title === 'string' ? data.title : data.id, // fallback to id if title is JSX
            mediaType: data.mediaType,
            isInternal: data.isInternal,
            personalLogin: data.personalLogin,
            category: typeof data.category === 'string' ? data.category : '',
            url: data.url,
            liked: data.liked,
            likeCount: data.likeCount,
            userProgressStatus: data.userProgressStatus,
            recommendationToHide: data.id,
          };

          return (
            <ResourceTableMobileItem
              key={data.id}
              data={data}
              handleViewClick={() => handleViewClick(baseData)}
              progress={progress}
              handleProgressChange={handleProgressChange}
              getProgressDropdownClass={getProgressDropdownClass}
              progressOptions={progressOptions}
              enableRatingSystem={enableRatingSystem}
              handleLikeClick={handleLikeClick}
              enableHideRecommendations={enableHideRecommendations}
              setRecommendationToHide={setRecommendationToHide}
              hideRecommendationModal={hideRecommendationModal}
              enableResourceBookmarking={enableResourceBookmarking}
              enableBookmarkDeleteColumn={enableBookmarkDeleteColumn}
              handleCreateBookmark={handleCreateBookmark}
              handleDeleteBookmark={handleDeleteBookmark}
            />
          );
        })}

        {enhancedTableData.length === 0 && (
          <div className="resourceTableEmpty">
            <Typography
              variant="body.small"
              fontSize={'14px'}
            >
              {text.emptyOptions}
            </Typography>
          </div>
        )}
      </div>
      {resources.length > 0 &&
        (defaultPagination ? (
          <Paginator
            className="resourceTableMobilePaginator"
            first={first}
            rows={rowsToShow}
            totalRecords={enhancedTableData.length}
            onPageChange={(e) => {
              if (setCurrentPage) setCurrentPage(e.page);
              setFirst(e.first);
            }}
          />
        ) : (
          <Paginator
            className="resourceTableMobilePaginator"
            first={currentPage !== undefined ? currentPage * rowsToShow : 0}
            rows={rowsToShow}
            totalRecords={totalItems}
            onPageChange={(e) => setCurrentPage?.(e.page)}
          />
        ))}

      <ResourceTypeModal
        isOpen={resourceTypeModal.value}
        onClose={resourceTypeModal.onFalse}
      />
      <ResourceLocationModal
        isOpen={resourceLocationModal.value}
        onClose={resourceLocationModal.onFalse}
      />
      <ProgressInfoModal
        isOpen={progressModal.value}
        onClose={progressModal.onFalse}
      />
      <LikesInfoModal
        isOpen={likesModal.value}
        onClose={likesModal.onFalse}
      />
      <HideRecommendationModal
        isOpen={hideRecommendationModal.value}
        onClose={hideRecommendationModal.onFalse}
        onConfirm={() => confirmHideRecommendation(recommendationToHide)}
      />
      <Tooltip
        target=".hasTooltip"
        appendTo={() => document.body}
        className="xxSmallTooltip"
      />
      <ContinueModal
        isOpen={selectedExternalResource !== null}
        onContinue={handleExternalContinue}
        onClose={() => setSelectedExternalResource(null)}
        title={externalResourceModal.title}
        body={externalResourceModal.body}
        primaryButtonText={externalResourceModal.continue}
        secondaryButtonText={externalResourceModal.back}
      />
      <EvaluateResourceModal
        isOpen={evaluateResourceModalOpen}
        onContinue={() => {
          if (resourceToEvaluate !== null) {
            handleLikeClick(resourceToEvaluate);
            setEvaluateResourceModalOpen(false);
            setResourceToEvaluate(null);
          }
        }}
        onClose={() => setEvaluateResourceModalOpen(false)}
      />
    </div>
  );
};

// ----------------------------------------------------------

const ResourceTableMobileItem = ({
  data,
  handleViewClick,
  progress,
  handleProgressChange,
  getProgressDropdownClass,
  progressOptions,
  enableRatingSystem,
  handleLikeClick,
  enableHideRecommendations,
  setRecommendationToHide,
  hideRecommendationModal,
  enableResourceBookmarking,
  enableBookmarkDeleteColumn,
  handleCreateBookmark,
  handleDeleteBookmark,
}: {
  data: EnhancedResourceTableData;
  handleViewClick: VoidFunction;
  progress: { [id: string]: string };
  handleProgressChange: (id: string, value: ProgressStatus) => void;
  getProgressDropdownClass: (value: string) => string;
  progressOptions: { label: string; value: string }[];
  enableRatingSystem?: boolean;
  handleLikeClick?: (id: string) => void;
  enableHideRecommendations?: boolean;
  setRecommendationToHide?: (id: string) => void;
  hideRecommendationModal?: { onTrue: () => void };
  enableResourceBookmarking?: boolean;
  enableBookmarkDeleteColumn?: boolean;
  handleCreateBookmark?: (id: string, title: string, isBookmarked: boolean) => void;
  handleDeleteBookmark?: (id: string, title: string, isBookmarked: boolean) => void;
}) => {
  return (
    <div className="resourceTableMobileItem">
      <div>{data.title}</div>
      <div>{data.category}</div>

      <div className="resourceTableMobileItemFlexRow">
        <div className="flexRow">
          <Typography
            variant="body.small"
            fontWeight={700}
          >
            {text.headers.resourceType}:
          </Typography>
          <Typography variant="body.small">{data.mediaType}</Typography>
        </div>
        <div className="flexRow">
          <Typography
            variant="body.small"
            fontSize={'14px'}
            fontWeight={700}
          >
            {text.headers.resourceLocation}:
          </Typography>
          <Typography
            variant="body.small"
            fontSize={'14px'}
          >
            {getLocationText(data.isInternal, data.personalLogin)}
          </Typography>
        </div>
      </div>

      <div className="resourceTableMobileItemActions">
        <Dropdown
          value={progress[data.id] || 'NOT_STARTED'}
          options={progressOptions}
          optionLabel="label"
          optionValue="value"
          onChange={(e) => handleProgressChange(data.id, e.value)}
          placeholder="Select"
          className={getProgressDropdownClass(progress[data.id] || 'NOT_STARTED')}
          appendTo={document.body}
          itemTemplate={(option) => (
            <div
              className="progressDropdownItem"
              key={option.value}
            >
              <Typography
                variant="body.xs"
                className="progressDropdownText"
              >
                {option.label}
              </Typography>
              <div className="dropdownSpacer" />
            </div>
          )}
        />

        {enableRatingSystem && handleLikeClick && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
            <span
              className="ratingBox thumbIcon"
              style={{ cursor: 'pointer' }}
              onClick={() => handleLikeClick(data.id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleLikeClick(data.id);
                }
              }}
              role="button"
              tabIndex={0}
            >
              <Typography
                variant="body.small"
                className="ratingsNumber"
              >
                {data.likeCount}
              </Typography>
              <i className={`fa-${data.liked ? 'solid' : 'regular'} fa-thumbs-up thumbIcon`} />
            </span>
          </div>
        )}
        {enableResourceBookmarking && handleCreateBookmark && (
          <BookmarkColumnBody
            onClick={() => {
              if (!data.isBookmarked) {
                handleCreateBookmark(data.id, data.titleString, data.isBookmarked);
              }
            }}
            text={{
              btnToolTipText: `${data.isBookmarked ? '' : text.tooltips.createBookmark}`,
              btnClassName: `bookmarkResourceBtn ${data.isBookmarked ? 'isActive' : ''}`,
              btnAriaLabel: data.isBookmarked
                ? text.accessibility.bookmarkedResource
                : text.accessibility.bookmarkResource,
            }}
          />
        )}

        {enableBookmarkDeleteColumn && handleDeleteBookmark && (
          <RemoveBookmarkColumnBody
            data-pr-tooltip={APP_TEXT.components.resourceTable.tooltips.removeBookmark}
            data-pr-showdelay={500}
            text={{
              btnAriaLabel: text.accessibility.removeBookmarkedResource,
              btnClassName: 'removeBookmarkBtn',
              btnTooltipText: text.tooltips.removeBookmark,
            }}
            aria-label={text.accessibility.removeBookmarkedResource}
            onClick={() => {
              handleDeleteBookmark(data.id, data.titleString, data.isBookmarked);
            }}
          />
        )}
      </div>

      {enableHideRecommendations && setRecommendationToHide && hideRecommendationModal && (
        <button
          id="hideBtnWrapper"
          data-pr-tooltip={APP_TEXT.components.resourceTable.tooltips.hideRecommendation}
          data-pr-showdelay={1000}
          tabIndex={0}
          aria-label={text.accessibility.hideRecommendationIcon}
          onClick={() => {
            setRecommendationToHide(data.id);
            hideRecommendationModal.onTrue();
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setRecommendationToHide(data.id);
              hideRecommendationModal.onTrue();
            }
          }}
        >
          <Typography
            variant="body.xs"
            className="hideRecommendationText"
          >
            {text.hideRecommendation}
          </Typography>
        </button>
      )}
    </div>
  );
};
